import { graphql } from 'react-relay';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useAddCommentPermissionCheckPermissionCheckAddCommentQuery as useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
	useAddCommentPermissionCheckPermissionCheckAddCommentQueryVariables as useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables,
} from './__types__/useAddCommentPermissionCheckPermissionCheckAddCommentQuery.ts';
const useAddCommentPermissionCheckPermissionCheckAddCommentQuery = graphql`
	query useAddCommentPermissionCheckPermissionCheckAddCommentQuery($contentId: ID!) {
		content(id: $contentId, status: ["draft", "current"]) {
			nodes {
				id
				type
				operations {
					operation
					targetType
				}
			}
		}
	}
`;

const noOpRefetch = () => Promise.resolve();

export const useAddCommentPermissionCheck = (contentId: string, cacheOnly = false) => {
	const baseOptions = {
		variables: { contentId },
		skip: !contentId,
	};

	const queryResult = expValEquals('confluence_frontend_stop_watching_queries', 'isEnabled', true)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<
				useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
				useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables
			>(useAddCommentPermissionCheckPermissionCheckAddCommentQuery, baseOptions)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<
				useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
				useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables
			>(useAddCommentPermissionCheckPermissionCheckAddCommentQuery, {
				...baseOptions,
				notifyOnNetworkStatusChange: true,
				fetchPolicy: cacheOnly ? 'cache-only' : 'cache-and-network',
			});

	const { data, loading, error } = queryResult;
	const networkStatus =
		'networkStatus' in queryResult && queryResult.networkStatus !== undefined
			? queryResult.networkStatus
			: NetworkStatus.ready;
	const refetch =
		'refetch' in queryResult && typeof queryResult.refetch === 'function'
			? queryResult.refetch
			: noOpRefetch;

	const contentNode = data?.content?.nodes?.[0];
	const operations = contentNode?.operations ?? [];

	const createPermission = operations.some(
		(operationResults) =>
			operationResults?.operation === 'create' && operationResults?.targetType === 'comment',
	);

	return {
		canAddComments: createPermission,
		loading,
		contentQueryError: error,
		refetch,
		networkStatus,
	};
};
